import { Component, Input, OnDestroy } from '@angular/core';
import { BaseCellComponent } from '../base-cell.component';
import { ViewManager } from 'src/app/board/pages/admin/admin-components/custom-views-config/view-builder/models/view-builder.models';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { FileManagementService } from 'src/app/services/file-management.service';
import { Subject, takeUntil, debounceTime, switchMap, EMPTY } from 'rxjs';

@Component({
  selector: 'bhub-pdf-image-cell',
  standalone: false,
  templateUrl: './pdf-image-cell.component.html',
  styleUrls: ['./pdf-image-cell.component.scss']
})
export class PdfImageCellComponent extends BaseCellComponent implements OnDestroy {
  @Input() viewManager: ViewManager | null = null;

  imageSource: 'static' | 'context' = 'static';
  filePath: string = '';
  safeImageUrl: SafeUrl | null = null;
  isLoadingImage: boolean = false;
  imageLoadError: boolean = false;

  private destroy$ = new Subject<void>();
  private currentImagePath: string = '';
  private loadImage$ = new Subject<string>();

  constructor(
    private sanitizer: DomSanitizer,
    private fileService: FileManagementService
  ) {
    super();

    // Set up debounced image loading to prevent duplicate requests
    this.loadImage$
      .pipe(
        debounceTime(100),
        switchMap((filePath: string) => {
          if (filePath === this.currentImagePath && this.safeImageUrl) {
            // Don't reload if it's the same image and already loaded
            return EMPTY;
          }
          this.currentImagePath = filePath;

          if (!filePath) {
            this.setImageUrl('');
            return EMPTY;
          }

          // Determine storage location from file path
          const fromAdmin = filePath.startsWith('admin/') || filePath.startsWith('_admin/');
          const fromOrganization = filePath.startsWith('organization/');

          this.isLoadingImage = true;
          return this.fileService.getSignedUrl(filePath, fromAdmin, fromOrganization, 60);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (response) => {
          if (response && response.url) {
            // Check if it's a local file URL (development environment)
            if (response.url.startsWith('file://')) {
              // For local development, use the download endpoint to get blob
              const fromAdmin = this.currentImagePath.startsWith('admin/') || this.currentImagePath.startsWith('_admin/');
              this.loadImageAsBlob(this.currentImagePath, fromAdmin);
            } else {
              // Production: use the signed URL directly
              this.isLoadingImage = false;
              this.setImageUrl(response.url);
            }
          } else if (response) {
            this.isLoadingImage = false;
            this.imageLoadError = true;
          }
        },
        error: (error) => {
          this.isLoadingImage = false;
          this.imageLoadError = true;
          console.error('Error fetching signed URL for PDF image:', this.currentImagePath, error);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected initializeCell(): void {
    this.updateDisplayProperties();
  }

  protected onCellOrDataChange(): void {
    this.updateDisplayProperties();
  }

  protected onCellUpdate(): void {
    this.updateDisplayProperties();
  }

  protected onDataSet(data: any): void {
    this.updateDisplayProperties();
  }

  reloadData(): void {
    this.updateDisplayProperties();
  }

  private updateDisplayProperties(): void {
    if (!this.cell) return;

    this.imageSource = this.getConfigValue('pdfImageSource', 'static') as 'static' | 'context';

    let newFilePath = '';

    // Resolve image path based on source
    if (this.imageSource === 'static') {
      newFilePath = this.getConfigValue('pdfFilePath', '');
    } else if (this.imageSource === 'context') {
      // Get from context data
      const contextVariable = this.getDataBindingValue('contextVariable');
      const fieldName = this.getDataBindingValue('fieldName');

      if (contextVariable && fieldName && this.viewManager?.contextData) {
        const dataObject = this.viewManager.contextData[contextVariable];
        if (dataObject) {
          if (Array.isArray(dataObject) && dataObject.length > 0) {
            newFilePath = dataObject[0][fieldName] || '';
          } else if (typeof dataObject === 'object') {
            newFilePath = dataObject[fieldName] || '';
          }
        }
      }
    }

    this.filePath = newFilePath;

    // Load the image if path changed
    if (newFilePath !== this.currentImagePath) {
      this.loadImage(newFilePath);
    }
  }

  private loadImage(filePath: string): void {
    this.imageLoadError = false;
    // Emit to the debounced Subject to load the image
    this.loadImage$.next(filePath);
  }

  private loadImageAsBlob(filePath: string, fromAdmin: boolean): void {
    this.fileService.downloadFile(filePath, fromAdmin)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob: Blob) => {
          this.isLoadingImage = false;
          const objectUrl = window.URL.createObjectURL(blob);
          this.setImageUrl(objectUrl);
        },
        error: (error) => {
          this.isLoadingImage = false;
          this.imageLoadError = true;
          console.error('Error loading PDF image as blob:', filePath, error);
        }
      });
  }

  private setImageUrl(url: string): void {
    if (url) {
      this.safeImageUrl = this.sanitizer.bypassSecurityTrustUrl(url);
    } else {
      this.safeImageUrl = null;
    }
  }

  onImageError(): void {
    this.imageLoadError = true;
  }

  onImageLoad(): void {
    this.imageLoadError = false;
  }

  hasImage(): boolean {
    return !!this.safeImageUrl && !this.imageLoadError;
  }
}
